import {
  isContestantData,
  isMemberData,
  isDomainShortcutData,
  isSportData,
} from './suggestionTypeGuards';

export interface SuggestionWithData {
  data?: unknown;
  label: string;
}

/**
 * Extracts a clean value from a suggestion data object.
 * This utility provides type-safe extraction of values from autocomplete suggestions.
 */
export const extractValueFromSuggestion = (suggestion: SuggestionWithData): string => {
  // Extract the actual name/value from the data, not the formatted label
  if (suggestion.data) {
    // For domain shortcuts, use the formatted domain shortcut value
    if (isDomainShortcutData(suggestion.data) && suggestion.data.domainShortcutValue) {
      return suggestion.data.domainShortcutValue;
    }

    // For members (players), extract number and name if available
    if (isMemberData(suggestion.data)) {
      const { name, number } = suggestion.data;
      if (number && number !== '' && name) {
        return `${number} ${name}`;
      }
      if (name) {
        return name;
      }
    }

    // For contestants (teams), extract name or shortName
    if (isContestantData(suggestion.data)) {
      return suggestion.data.shortName || suggestion.data.name || '';
    }

    // For sport info
    if (isSportData(suggestion.data) && suggestion.data.sport) {
      return suggestion.data.sport;
    }
  }

  // Fallback to label if no specific data structure
  return suggestion.label;
};

/**
 * Simplified version for repeater cells that excludes number formatting.
 * Used in repeater components where player numbers are not needed.
 */
export const extractValueFromSuggestionSimple = (suggestion: SuggestionWithData): string => {
  // Extract the actual name/value from the data, not the formatted label
  if (suggestion.data) {
    // For domain shortcuts, use the formatted domain shortcut value
    if (isDomainShortcutData(suggestion.data) && suggestion.data.domainShortcutValue) {
      return suggestion.data.domainShortcutValue;
    }

    // For members (players), extract just the name (no number)
    if (isMemberData(suggestion.data) && suggestion.data.name) {
      return suggestion.data.name;
    }

    // For contestants (teams), extract name or shortName
    if (isContestantData(suggestion.data)) {
      return suggestion.data.shortName || suggestion.data.name || '';
    }

    // For sport info
    if (isSportData(suggestion.data) && suggestion.data.sport) {
      return suggestion.data.sport;
    }
  }

  // Fallback to label if no specific data structure
  return suggestion.label;
};
